import type { REST_schema } from "../../@types/sharedSchema";

/**
 * The FAIL-CLOSED blocked-numbers gate for every WhatsApp-Web (extension) send loop.
 *
 * Every bulk send through the Chrome extension MUST resolve its recipient list against the
 * server's blocked-numbers list (`POST /whatsapp/blacklist/check`) before sending — the local
 * contact flags in `getSkipContactReasons` are only a synchronous convenience layer: they miss
 * org-level blocks entirely and cover NO recipient that isn't in the caller's contact book
 * (which is exactly the number an opt-out most needs to protect).
 *
 * FAIL CLOSED: `checkBlockedNumbers` THROWS `BlockedCheckFailedError` when the check cannot be
 * completed (network failure, non-2xx, unexpected shape). A caller that catches it must ABORT
 * the send — "the check failed" never means "assume everyone is clear". Messaging a number on
 * the list carries statutory exposure under Israel's spam law (Amendment 40).
 *
 * Long runs should RE-CHECK the remaining tail at natural pause points (the cooldown breaks in
 * the bulk dialog): a number blocked mid-run must stop receiving the rest of the run.
 */

export class BlockedCheckFailedError extends Error {
  constructor(cause?: unknown) {
    super("בדיקת המספרים החסומים נכשלה — השליחה בוטלה");
    this.name = "BlockedCheckFailedError";
    if (cause instanceof Error && cause.message) {
      this.message += ` (${cause.message})`;
    }
  }
}

/** Server cap is 5,000/request; we stay well under it so one slow chunk can't stall the run. */
const CHUNK_SIZE = 500;

export type BlockedCheckFn = (
  request: REST_schema.WhatsappBlacklistCheckRequest
) => Promise<REST_schema.WhatsappBlacklistCheckResponse>;

/**
 * Checks every phone against the server's blocked list. Returns a map keyed by the EXACT string
 * that was passed in (so callers can look up their own identifiers), with the server's normalized
 * row as the value. Numbers the server normalizes differently are matched back best-effort by
 * digit suffix.
 *
 * @throws BlockedCheckFailedError — caller must abort the send.
 */
export const checkBlockedNumbers = async (
  check: BlockedCheckFn,
  phones: readonly string[]
): Promise<Map<string, REST_schema.WhatsappBlacklistCheckBlockedRow>> => {
  const unique = [...new Set(phones.filter((p) => typeof p === "string" && p.trim().length > 0))];
  const blocked = new Map<string, REST_schema.WhatsappBlacklistCheckBlockedRow>();
  if (unique.length === 0) {
    return blocked;
  }

  let rows: REST_schema.WhatsappBlacklistCheckBlockedRow[];
  try {
    const chunks: string[][] = [];
    for (let i = 0; i < unique.length; i += CHUNK_SIZE) {
      chunks.push(unique.slice(i, i + CHUNK_SIZE));
    }
    const responses = await Promise.all(chunks.map((chunk) => check({ phones: chunk })));
    rows = responses.flatMap((r) => {
      if (!r || !Array.isArray(r.blocked)) {
        // An unexpected shape is a failed check, not an empty one.
        throw new Error("unexpected /blacklist/check response shape");
      }
      return r.blocked;
    });
  } catch (err) {
    throw new BlockedCheckFailedError(err);
  }

  if (rows.length === 0) {
    return blocked;
  }

  // Index the server rows by their last-9-digits so '0501234567', '+972501234567' and
  // '972501234567' all resolve to the same verdict regardless of which form the caller sent.
  const digitsKey = (value: string): string => value.replace(/\D/g, "").slice(-9);
  const byDigits = new Map<string, REST_schema.WhatsappBlacklistCheckBlockedRow>();
  for (const row of rows) {
    byDigits.set(digitsKey(row.phone), row);
  }
  for (const original of unique) {
    const row = byDigits.get(digitsKey(original));
    if (row) {
      blocked.set(original, row);
    }
  }
  return blocked;
};
